import React from 'react';
import {
  ArrowLeft,
  Award,
  BookOpen,
  CalendarDays,
  Clock3,
  Crown,
  FolderOpen,
  LogOut,
  Receipt,
  Search,
  Shield,
} from 'lucide-react';

import type { FeatureRemainingSummary, StoredUser } from '../lib/api';
import { getPanRecordNoteCount } from './pan/pan-persistence';

interface MinePanelProps {
  mineSubPage: 'home' | 'notice' | 'account' | 'display' | 'qimen' | 'archive';
  setMineSubPage: (page: 'home' | 'notice' | 'account' | 'display' | 'qimen' | 'archive') => void;
  userProfile: StoredUser | null;
  userInitial: string;
  memberActive: boolean;
  membershipStatus: string;
  currentPointsText: string;
  expireTimeText: string;
  featureRemainingSummaryText: string;
  showFeatureRemaining: boolean;
  featureRemainingError: string;
  featureRemainingLoading: boolean;
  featureRemainingSummary: FeatureRemainingSummary | null;
  accountNameDraft: string;
  setAccountNameDraft: (value: string) => void;
  accountEmailDraft: string;
  setAccountEmailDraft: (value: string) => void;
  accountPhoneDraft: string;
  setAccountPhoneDraft: (value: string) => void;
  accountSaveResult: { ok: boolean; msg: string } | null;
  onSaveAccountSettings: () => void;
  clientLogSubmitting: boolean;
  clientLogSubmitResult: { ok: boolean; msg: string; logId?: string } | null;
  onSubmitClientLog: () => void;
  onToggleFeatureRemaining: () => void;
  onOpenArchive: () => void;
  onOpenArchiveRecord: (type: 'qimen' | 'daluren' | 'sancai', recordId: string) => void;
  onOpenCourse: () => void;
  onOpenCertificate: () => void;
  onOpenOrder: () => void;
  onLogout: () => void;
  renderSettingsSections: () => React.ReactNode;
  renderDisplaySettingsContent: () => React.ReactNode;
  renderQimenSettingsContent: () => React.ReactNode;
  renderActivationBlock: () => React.ReactNode;
}

const MinePanel: React.FC<MinePanelProps> = ({
  mineSubPage,
  setMineSubPage,
  userProfile,
  userInitial,
  memberActive,
  membershipStatus,
  currentPointsText,
  expireTimeText,
  featureRemainingSummaryText,
  showFeatureRemaining,
  featureRemainingError,
  featureRemainingLoading,
  featureRemainingSummary,
  accountNameDraft,
  setAccountNameDraft,
  accountEmailDraft,
  setAccountEmailDraft,
  accountPhoneDraft,
  setAccountPhoneDraft,
  accountSaveResult,
  onSaveAccountSettings,
  clientLogSubmitting,
  clientLogSubmitResult,
  onSubmitClientLog,
  onToggleFeatureRemaining,
  onOpenArchive,
  onOpenArchiveRecord,
  onOpenCourse,
  onOpenCertificate,
  onOpenOrder,
  onLogout,
  renderSettingsSections,
  renderDisplaySettingsContent,
  renderQimenSettingsContent,
  renderActivationBlock,
}) => {
  const formatArchiveTime = (raw: unknown): string => {
    const ts = Number(raw || 0);
    if (!Number.isFinite(ts) || ts <= 0) return '--';
    return new Date(ts).toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const readArray = (key: string): any[] => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  type ArchiveType = 'qimen' | 'daluren' | 'sancai';
  type ArchiveFilterType = 'all' | ArchiveType;
  type ArchiveRow = {
    id: string;
    title: string;
    subtitle: string;
    createdAt: number;
    type: ArchiveType;
    typeLabel: string;
    tagChar: string;
    timeText: string;
    aiUsed?: boolean;
    noteCount?: number;
  };

  const formatAgo = (raw: unknown): string => {
    const ts = Number(raw || 0);
    if (!Number.isFinite(ts) || ts <= 0) return '--';
    const diff = Date.now() - ts;
    if (diff < 60 * 60 * 1000) return `${Math.max(1, Math.floor(diff / 60000))}分钟前`;
    if (diff < 24 * 60 * 60 * 1000) return `${Math.max(1, Math.floor(diff / (60 * 60 * 1000)))}小时前`;
    if (diff < 30 * 24 * 60 * 60 * 1000) return `${Math.max(1, Math.floor(diff / (24 * 60 * 60 * 1000)))}天前`;
    return formatArchiveTime(ts);
  };

  const qimenRows: ArchiveRow[] = readArray('qimen_pan_records_v2')
    .map((item) => ({
      id: String(item?.id || ''),
      title: String(item?.question || '').trim() || '未填写事项',
      subtitle: `${item?.panMode === 'ke' ? '刻盘' : '时盘'} · 奇门排盘 · ${getPanRecordNoteCount(item)} 条笔记`,
      createdAt: Number(item?.updatedAt || item?.createdAt || 0),
      type: 'qimen' as const,
      typeLabel: '奇门',
      tagChar: '奇',
      timeText: String(item?.sourceDateTime || '').replace('T', ' ') || formatArchiveTime(item?.createdAt),
      aiUsed: Boolean(String(item?.aiReport || '').trim()) || (Array.isArray(item?.aiFollowupHistory) && item.aiFollowupHistory.length > 0),
      noteCount: getPanRecordNoteCount(item),
    }))
    .filter((item) => item.id)
    .sort((a, b) => b.createdAt - a.createdAt);

  const dalurenRows: ArchiveRow[] = readArray('daluren_pan_records_v1')
    .map((item) => ({
      id: String(item?.id || ''),
      title: String(item?.options?.question || '').trim() || '未填写事项',
      subtitle: '大六壬起课',
      createdAt: Number(item?.createdAt || 0),
      type: 'daluren' as const,
      typeLabel: '大六壬',
      tagChar: '壬',
      timeText: String(item?.options?.dateTimeValue || '').replace('T', ' ') || formatArchiveTime(item?.createdAt),
      aiUsed: Boolean(String(item?.aiReport || item?.result?.aiReport || '').trim()),
    }))
    .filter((item) => item.id)
    .sort((a, b) => b.createdAt - a.createdAt);

  const sancaiRows: ArchiveRow[] = readArray('qimen_sancai_liuge_records_v1')
    .map((item) => ({
      id: String(item?.id || ''),
      title: `${String(item?.surname || '')}${String(item?.givenName || '')}`.trim() || '未命名记录',
      subtitle: `三才六格 · ${String(item?.gender === 'female' ? '女' : '男')}`,
      createdAt: Number(item?.updatedAt || item?.createdAt || 0),
      type: 'sancai' as const,
      typeLabel: '姓名学',
      tagChar: '名',
      timeText: formatArchiveTime(item?.updatedAt || item?.createdAt),
      aiUsed: false,
    }))
    .filter((item) => item.id)
    .sort((a, b) => b.createdAt - a.createdAt);

  const archiveRows: ArchiveRow[] = [...qimenRows, ...dalurenRows, ...sancaiRows].sort((a, b) => b.createdAt - a.createdAt);
  const [activeArchiveType, setActiveArchiveType] = React.useState<ArchiveFilterType>('all');
  const [archiveKeyword, setArchiveKeyword] = React.useState('');
  const [showMemberStats, setShowMemberStats] = React.useState(false);
  const normalizedArchiveKeyword = archiveKeyword.trim().toLowerCase();
  const filteredArchiveRows = archiveRows.filter((row) => {
    if (activeArchiveType !== 'all' && row.type !== activeArchiveType) return false;
    if (!normalizedArchiveKeyword) return true;
    const text = `${row.title} ${row.subtitle} ${row.typeLabel} ${row.timeText}`.toLowerCase();
    return text.includes(normalizedArchiveKeyword);
  });
  const handleToggleMemberStats = () => {
    const next = !showMemberStats;
    setShowMemberStats(next);
    if (next !== showFeatureRemaining) {
      onToggleFeatureRemaining();
    }
  };

  if (mineSubPage === 'display') {
    return (
      <div className="text-left pb-2">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setMineSubPage('home')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            返回我的
          </button>
          <h3 className="text-base font-semibold text-slate-800">显示设置</h3>
        </div>
        {renderDisplaySettingsContent()}
      </div>
    );
  }

  if (mineSubPage === 'qimen') {
    return (
      <div className="text-left pb-3">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setMineSubPage('home')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            返回我的
          </button>
          <h3 className="text-base font-semibold text-slate-800">奇门设置</h3>
        </div>
        {renderQimenSettingsContent()}
      </div>
    );
  }

  if (mineSubPage === 'archive') {
    return (
      <div className="text-left pb-3 pt-0">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-[#faf9f7]/95 px-3 py-1.5 backdrop-blur">
          <button
            type="button"
            onClick={() => setMineSubPage('home')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            返回我的
          </button>
          <h3 className="text-base font-semibold text-slate-800">档案</h3>
        </div>

        <div className="pt-3">
          <div className="text-sm font-semibold text-slate-700">我的档案</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {[
              { id: 'all' as const, label: '全部', count: archiveRows.length },
              { id: 'qimen' as const, label: '奇门', count: qimenRows.length },
              { id: 'daluren' as const, label: '六壬', count: dalurenRows.length },
              { id: 'sancai' as const, label: '姓名', count: sancaiRows.length },
            ].map((stat) => {
              const active = activeArchiveType === stat.id;
              return (
                <button
                  key={stat.id}
                  type="button"
                  onClick={() => setActiveArchiveType(stat.id)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active
                      ? 'border-[#A9C6F8] bg-[#EAF2FF] text-[#2E5FB9]'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {stat.label}
                  <span className={`ml-1.5 ${active ? 'text-[#2E5FB9]' : 'text-slate-500'}`}>{stat.count}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-3 rounded-lg border border-slate-300 px-3 py-2.5 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              type="text"
              value={archiveKeyword}
              onChange={(e) => setArchiveKeyword(e.target.value)}
              placeholder="搜索标题、问题、姓名..."
              className="w-full bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
            />
          </div>
        </div>

        <div className="mt-3 text-xs text-slate-500 flex items-center justify-end gap-1.5">
          <Clock3 className="w-3.5 h-3.5" />
          按时间倒序
        </div>

        <div className="mt-2 border-t border-slate-200">
          {filteredArchiveRows.length === 0 ? (
            <div className="py-8 text-sm text-slate-500 text-center">暂无匹配记录</div>
          ) : (
            filteredArchiveRows.map((row) => (
              <button
                key={`${row.type}-${row.id}`}
                type="button"
                onClick={() => onOpenArchiveRecord(row.type, row.id)}
                className="w-full border-b border-slate-200 py-3 hover:bg-slate-50/80 transition-colors text-left"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-lg bg-[#EEF3FF] text-[#2E5FB9] flex items-center justify-center text-xl font-bold shrink-0">
                      {row.tagChar}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[15px] font-medium text-slate-800 truncate">{row.title}</div>
                      <div className="text-sm text-slate-500 mt-0.5 flex items-center gap-2">
                        <span>{row.typeLabel}</span>
                        <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${row.aiUsed ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {row.aiUsed ? 'AI解局' : '未AI'}
                        </span>
                        {row.type === 'qimen' && (
                          <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${Number(row.noteCount || 0) > 0 ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                            笔记 {Number(row.noteCount || 0)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 shrink-0">{formatAgo(row.createdAt)}</div>
                </div>

                <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-400">
                  <div className="min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <div className="inline-flex items-center gap-1">
                      <CalendarDays className="w-3.5 h-3.5" />
                      {row.timeText}
                    </div>
                    <div className="truncate">{row.subtitle}</div>
                  </div>
                  <div className="shrink-0 text-[11px] font-medium text-[#2E5FB9]">查看详情</div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  if (mineSubPage === 'notice') {
    return (
      <div className="text-left pb-2">
        <div className="sticky top-0 z-10 -mx-1 flex items-center justify-between border-b border-slate-200 bg-[#faf9f7]/95 px-1 py-1.5 backdrop-blur">
          <button
            type="button"
            onClick={() => setMineSubPage('home')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            返回我的
          </button>
          <h3 className="text-base font-semibold text-slate-800">用户须知</h3>
        </div>

        <div className="pt-0 space-y-3 text-sm text-slate-600 leading-7">
          <p>
            欢迎使用“轩辕国学”（以下简称“本平台”）。本须知用于说明您在使用本平台各项服务时的权利、义务与注意事项。
            使用本平台即表示您已阅读并同意本须知内容。
          </p>

          <p className="font-semibold text-slate-700">一、平台定位</p>
          <p>本平台提供奇门遁甲、三才姓名学、心理测评、学习资料与 AI 辅助解读等功能，定位为传统文化学习与信息参考服务。</p>
          <p>平台所有解读内容均用于学习交流与辅助判断，不构成医疗、法律、投资、婚恋等专业决策依据。</p>

          <p className="font-semibold text-slate-700">二、账号与安全</p>
          <p>您应确保注册信息真实、有效，并妥善保管账号、验证码与登录设备。</p>
          <p>因您主动泄露、转借账号或设备管理不当造成的风险与损失，由您自行承担。</p>

          <p className="font-semibold text-slate-700">三、使用行为规范</p>
          <p>您不得利用本平台发布违法、侵权、诈骗、色情、暴力、恶意营销或其他违反公序良俗的信息。</p>
          <p>您不得攻击、爬取、干扰本平台服务，不得批量盗用平台内容进行商业复刻。</p>

          <p className="font-semibold text-slate-700">四、会员与功能权益</p>
          <p>会员权益、积分或次数限制以平台当期规则为准。若规则调整，平台将以站内公告或页面提示方式更新。</p>
          <p>通过活动、测试或赠送方式获得的权益，平台可在规则范围内设置有效期与使用边界。</p>

          <p className="font-semibold text-slate-700">五、AI 解读与结果声明</p>
          <p>AI 输出存在不确定性，可能因输入信息、知识库版本与模型状态产生差异，请结合现实情况独立判断。</p>
          <p>涉及健康、金融、法律、重大人生决策时，请务必咨询对应专业人士。</p>

          <p className="font-semibold text-slate-700">六、隐私与数据</p>
          <p>为提供服务，本平台可能保存您主动提交的排盘参数、测试结果与操作记录，用于功能实现、问题排查与体验优化。</p>
          <p>除法律法规要求或经您授权外，本平台不会向无关第三方出售您的个人数据。</p>

          <p className="font-semibold text-slate-700">七、知识产权</p>
          <p>本平台的页面结构、视觉设计、程序代码、文案与整理后的数据内容，均受相关法律保护。</p>
          <p>未经许可，不得以抓取、镜像、复制、二次分发等方式用于商业用途。</p>

          <p className="font-semibold text-slate-700">八、规则更新与争议处理</p>
          <p>本平台可根据运营需要更新本须知。更新后继续使用本平台视为您已接受新版本条款。</p>
          <p>如发生争议，双方应先行友好协商；协商不成，依法提交有管辖权的人民法院处理。</p>

          <p className="font-semibold text-slate-700">九、联系我们</p>
          <p>产品：轩辕国学</p>
          <p>联系邮箱：xuanyuan@aifashou.cn</p>
          <p>官方网站：qimen.aifashou.cn</p>
        </div>
      </div>
    );
  }

  if (mineSubPage === 'account') {
    return (
      <div className="text-left pb-3">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setMineSubPage('home')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            返回我的
          </button>
          <h3 className="text-base font-semibold text-slate-800">账号设置</h3>
        </div>

        <div className="border-t border-slate-200 pt-3 space-y-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">昵称</label>
            <input
              type="text"
              value={accountNameDraft}
              onChange={(e) => setAccountNameDraft(e.target.value)}
              placeholder="请输入昵称"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">邮箱</label>
            <input
              type="email"
              value={accountEmailDraft}
              onChange={(e) => setAccountEmailDraft(e.target.value)}
              placeholder="请输入邮箱"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">手机号</label>
            <input
              type="tel"
              value={accountPhoneDraft}
              onChange={(e) => setAccountPhoneDraft(e.target.value)}
              placeholder="请输入手机号"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
          {accountSaveResult && (
            <div
              className={`rounded-lg border px-3 py-2 text-xs ${accountSaveResult.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}
            >
              {accountSaveResult.msg}
            </div>
          )}
          <button
            type="button"
            onClick={onSaveAccountSettings}
            className="w-full rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            保存设置
          </button>
          <button
            type="button"
            onClick={onSubmitClientLog}
            disabled={clientLogSubmitting}
            className={`w-full rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
              clientLogSubmitting
                ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
            }`}
          >
            {clientLogSubmitting ? '诊断日志提交中...' : '提交诊断日志'}
          </button>
          {clientLogSubmitResult && (
            <div
              className={`rounded-lg border px-3 py-2 text-xs ${
                clientLogSubmitResult.ok ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-rose-200 bg-rose-50 text-rose-700'
              }`}
            >
              {clientLogSubmitResult.msg}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="text-center pt-0 pb-2">
      <div className="space-y-3 text-left">
        <div className={`relative overflow-hidden rounded-3xl p-4 sm:p-5 shadow-lg border ${memberActive ? 'bg-gradient-to-br from-[#faf8f5] via-[#f5f0e8] to-[#faf8f5] border-[#e8e4dc]' : 'bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 border-slate-200'}`}>
          <div className={`absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl ${memberActive ? 'bg-gradient-to-bl from-amber-600/5 to-transparent' : 'bg-gradient-to-bl from-slate-400/10 to-transparent'}`} />
          <div className={`absolute bottom-0 left-0 w-32 h-32 rounded-full blur-2xl ${memberActive ? 'bg-gradient-to-tr from-stone-400/5 to-transparent' : 'bg-gradient-to-tr from-slate-300/15 to-transparent'}`} />
          <div className={`absolute top-4 right-8 w-2 h-2 rounded-full ${memberActive ? 'bg-amber-500/20' : 'bg-slate-400/30'}`} />
          <div className={`absolute bottom-8 right-16 w-1.5 h-1.5 rounded-full ${memberActive ? 'bg-amber-600/15' : 'bg-slate-400/25'}`} />

          <div className="relative flex items-start gap-4">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold text-white shadow-md shrink-0 ${memberActive ? 'bg-gradient-to-br from-[#d4a574] to-[#b8941f] ring-4 ring-amber-100/50' : 'bg-gradient-to-br from-slate-400 to-slate-500 ring-4 ring-slate-200/80'}`}>
              {userInitial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className={`text-xl font-bold truncate ${memberActive ? 'text-[#3d2914]' : 'text-slate-700'}`}>{userProfile?.name}</h2>
                <div className="flex items-center gap-1.5 shrink-0">
                  {memberActive && (
                    <button
                      type="button"
                      onClick={handleToggleMemberStats}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-amber-300 bg-gradient-to-r from-amber-400 to-amber-500 text-white shadow-sm transition-colors"
                      aria-label={showMemberStats ? '隐藏会员详情' : '显示会员详情'}
                      title={showMemberStats ? '隐藏会员详情' : '显示会员详情'}
                    >
                      <Crown className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {userProfile?.isAdminMember && (
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-amber-400 bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-sm">
                      <Shield className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
              </div>
              <p className={`text-sm truncate mt-1 ${memberActive ? 'text-[#8b7355]' : 'text-slate-500'}`}>{userProfile?.email || userProfile?.userId}</p>
              <p className={`mt-1.5 text-[11px] font-normal truncate ${memberActive ? 'text-[#b8ab95]' : 'text-slate-400'}`}>到期时间：{expireTimeText}</p>
            </div>
          </div>

          {showMemberStats && (
            <div className="relative mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <div className="rounded-2xl bg-white/70 backdrop-blur-sm border border-[#e8e4dc] p-2.5 shadow-sm text-left min-h-[76px] flex flex-col justify-between">
                <p className="text-xs font-medium text-[#8b7355] leading-5">当前积分</p>
                <p className="text-sm font-semibold text-[#5c4033] leading-6">{currentPointsText}</p>
              </div>
              <button
                onClick={onToggleFeatureRemaining}
                className="rounded-2xl bg-white/70 backdrop-blur-sm border border-[#e8e4dc] p-2.5 shadow-sm text-left min-h-[76px] flex flex-col justify-between hover:bg-white transition-colors"
              >
                <p className="text-xs font-medium text-[#8b7355] leading-5">今日剩余</p>
                <p className="text-sm font-semibold text-[#5c4033] leading-6">{featureRemainingSummaryText}</p>
              </button>
            </div>
          )}

          {showMemberStats && showFeatureRemaining && (
            <div className="mt-3 rounded-xl bg-white/80 border border-[#e8e4dc] p-3">
              {featureRemainingError ? (
                <div className="text-xs text-rose-600">{featureRemainingError}</div>
              ) : featureRemainingLoading ? (
                <div className="text-xs text-slate-500">正在加载今日各功能剩余次数...</div>
              ) : featureRemainingSummary ? (
                <div>
                  <p className="text-xs text-slate-500 mb-2">
                    统计日期：{featureRemainingSummary.dateKey}（已用 {featureRemainingSummary.totalUsed} 次）
                  </p>
                  <div className="space-y-1.5">
                    {featureRemainingSummary.items.map((item) => (
                      <div key={item.featureId} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">{item.featureName}</span>
                        <span className="font-semibold text-slate-700">
                          {item.unlimited ? `不限（已用 ${item.used}）` : `${item.remaining}/${item.limit}（已用 ${item.used}）`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500">暂无可用数据</div>
              )}
            </div>
          )}
        </div>

        <div className="rounded-3xl bg-white p-4 shadow-sm border border-slate-100">
          <div className="grid grid-cols-4 gap-2.5">
            <button
              onClick={onOpenArchive}
              className="group flex flex-col items-center gap-2 p-2.5 rounded-2xl hover:bg-slate-50 transition-all duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200">
                <FolderOpen className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs font-medium text-slate-700">档案</span>
            </button>
            <button
              onClick={onOpenCourse}
              className="group flex flex-col items-center gap-2 p-2.5 rounded-2xl hover:bg-slate-50 transition-all duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs font-medium text-slate-700">课程</span>
            </button>
            <button
              onClick={onOpenCertificate}
              className="group flex flex-col items-center gap-2 p-2.5 rounded-2xl hover:bg-slate-50 transition-all duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200">
                <Award className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs font-medium text-slate-700">证书</span>
            </button>
            <button
              onClick={onOpenOrder}
              className="group flex flex-col items-center gap-2 p-2.5 rounded-2xl hover:bg-slate-50 transition-all duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs font-medium text-slate-700">订单</span>
            </button>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-3 sm:p-4 shadow-sm border border-slate-100 -mx-1 sm:mx-0">
          {renderSettingsSections()}
        </div>

        <div>{renderActivationBlock()}</div>
      </div>

      <button
        onClick={onLogout}
        className="w-full p-3 mt-4 bg-rose-50 rounded-xl border border-rose-100 font-semibold text-rose-600 hover:bg-rose-100 flex items-center justify-center gap-2"
      >
        <LogOut className="w-5 h-5" /> 退出登录
      </button>
    </div>
  );
};

export default MinePanel;
